import { NuxtApp } from "#app";
import {
  Configuration,
  ContactControllerApi,
  CustomerControllerApi,
  OrderControllerApi,
  ProductControllerApi,
  RegistrationControllerApi,
  VoucherControllerApi,
  OrganisationControllerApi,
  TermsConditionsControllerApi,
  InvoicesControllerApi,
  DatabaseControllerApi,
  InfoControllerApi,
  DatabaseTemplateControllerApi,
  DatabaseAdminControllerApi,
  MessageControllerApi,
  CartControllerApi,
  SubscriptionControllerApi,
  AccountControllerApi,
  DatabaseSpecialOpsControllerApi,
  DatabaseSyncControllerApi,
  PurchaseHistoryControllerApi, SmtpControllerApi,
} from '~/api-client';

/**
 * ══ DER SCHLUESSEL, MIT DEM EIN BESUCHER SEINEN EIGENEN EINTRAG SIEHT ══════
 *
 * Anonyme Nutzer teilen sich EIN Konto je Datenbank. Ihre eigene Abgabe
 * erkennt der Server deshalb nicht am Besitzer, sondern an einem Token am
 * Eintrag (`X-Anon-Token`, siehe `DatabasePermissionService`).
 *
 * BEFUND DES BETREIBERS (18.10.2026): „Wieso ist denn jetzt bei einem Link es
 * nicht vorausgefuellt?" — Bis hierher wuerfelte jeder Browser sein eigenes
 * Token. Auf einen Eintrag, den der Gastgeber fuer den Gast vorbereitet hat,
 * passte es nie: Der Server lieferte `[]`, die Karte blieb leer.
 *
 * DREI REGELN, in dieser Reihenfolge:
 *  1. Steht ein `anonToken` in der Adresse (so verschickt es die Einladung),
 *     gilt es — und wird FUER DIESE DATENBANK gemerkt.
 *  2. Sonst der gemerkte Schluessel dieser Datenbank, gesucht unter ihrer Id
 *     UND unter ihrer Adress-Kennung (eine Datenbank kann beides haben).
 *  3. Sonst der eigene Schluessel des Browsers; fehlt auch der, entsteht er.
 *
 * Der eigene Schluessel bleibt dabei erhalten: Wer anderswo schon etwas
 * abgegeben hat, verliert den Zugang dazu nicht. Ein Token oeffnet immer
 * genau EINEN Eintrag, fremde bleiben unsichtbar.
 */
export function besucherSchluessel(
  aufruf: string,
  pfad: string,
  suche: string,
  speicher: Pick<Storage, 'getItem' | 'setItem'>,
): string {
  const ausDerAnfrage = /\/databases\/([^/?#]+)/.exec(aufruf)?.[1];
  const ausDemPfad = /\/datenbank\/([^/?#]+)/.exec(pfad ?? '')?.[1];
  const namen = [ausDerAnfrage, ausDemPfad].filter(Boolean).map((x) => `dbg-anon-token:${x}`);

  let ausDerAdresse: string | null = null;
  try {
    ausDerAdresse = new URLSearchParams(suche ?? '').get('anonToken');
  } catch { /* keine lesbare Adresse — dann eben ohne */ }

  if (ausDerAdresse) {
    for (const name of namen) {
      speicher.setItem(name, ausDerAdresse);
    }
    return ausDerAdresse;
  }

  for (const name of namen) {
    const gemerkt = speicher.getItem(name);
    if (gemerkt) {
      return gemerkt;
    }
  }

  const eigener = speicher.getItem('dbg-anon-token');
  if (eigener) {
    return eigener;
  }
  const neuer = (globalThis.crypto?.randomUUID?.() ?? (Date.now() + '-' + Math.random().toString(36).slice(2)));
  speicher.setItem('dbg-anon-token', neuer);
  return neuer;
}

export class BackendService {
  private readonly _accountControllerApi: AccountControllerApi;
  private readonly _productControllerApi: ProductControllerApi;
  private readonly _customerControllerApi: CustomerControllerApi;
  private readonly _smtpControllerApi: SmtpControllerApi;
  private readonly _orderControllerApi: OrderControllerApi;
  private readonly _contactControllerApi: ContactControllerApi;
  private readonly _registrationControllerApi: RegistrationControllerApi;
  private readonly _voucherControllerApi: VoucherControllerApi;
  private readonly _organisationControllerApi: OrganisationControllerApi;
  private readonly _termsConditionsControllerApi: TermsConditionsControllerApi;
  private readonly _invoiceControllerApi: InvoicesControllerApi;
  private readonly _messageControllerApi: MessageControllerApi;
  private readonly _infoControllerApi: InfoControllerApi;
  private readonly _cartControllerApi: CartControllerApi;
  private readonly _subscriptionControllerApi: SubscriptionControllerApi;
  private readonly _purchaseHistoryControllerApi: PurchaseHistoryControllerApi;
  
  private readonly _databaseControllerApi: DatabaseControllerApi;
  private readonly _databaseTemplateControllerApi: DatabaseTemplateControllerApi;
  private readonly _databaseAdminControllerApi: DatabaseAdminControllerApi;
  private readonly _databaseSpecialOpsControllerApi: DatabaseSpecialOpsControllerApi;
  private readonly _databaseSyncControllerApi: DatabaseSyncControllerApi;

  constructor(nuxt: NuxtApp) {
    const config = new Configuration({
      basePath: nuxt.$config.public.basePath,
      accessToken: (_) => {
        if (import.meta.client) {
          const userSession = useAuth();
          if (userSession.data.value) {
            // console.log("access token backend: " +  (<any>userSession.data.value).accessToken );
            return (<any>userSession.data.value).accessToken;
          } 
        } else {
          const authBasePath = nuxt.$config.public.auth.basePath;
          const headers = nuxt.ssrContext?.event.headers;
          return $fetch(`${authBasePath}/session`, {
            headers
          }).then((value) => (value as any).accessToken);
        }
      },
      // Variante B: pro Browser ein unraetbares Session-Token als Header X-Anon-Token
      // mitsenden -> anonyme Nutzer lesen/aendern NUR ihre eigenen Abgaben (Server
      // matcht auf entry.anonToken; readOwnedEntries fuer Anon ist serverseitig aus).
      // Nur clientseitig (localStorage), persistiert -> Mehrseitig/Resume funktioniert.
      middleware: [{
        pre: async (context: any) => {
          // NUR auf Entry-Endpoints (/databases/.../entries...). Global wuerde der
          // Header bei JEDEM Request gesendet -> CORS-Preflight scheitert app-weit.
          if (import.meta.client && typeof context.url === 'string' && context.url.includes('/entries')) {
            const token = besucherSchluessel(
              context.url, window.location.pathname, window.location.search, window.localStorage);
            context.init.headers = { ...(context.init.headers ?? {}), 'X-Anon-Token': token };
          }
          return { url: context.url, init: context.init };
        }
      }] as any
    });
    this._accountControllerApi = new AccountControllerApi(config);
    this._productControllerApi = new ProductControllerApi(config);
    this._customerControllerApi = new CustomerControllerApi(config);
    this._smtpControllerApi = new SmtpControllerApi(config);
    this._orderControllerApi = new OrderControllerApi(config);
    this._contactControllerApi = new ContactControllerApi(config);
    this._registrationControllerApi = new RegistrationControllerApi(config);
    this._voucherControllerApi = new VoucherControllerApi(config);
    this._organisationControllerApi = new OrganisationControllerApi(config);
    this._termsConditionsControllerApi = new TermsConditionsControllerApi(config);
    this._invoiceControllerApi = new InvoicesControllerApi(config);
    this._infoControllerApi = new InfoControllerApi(config);
    this._messageControllerApi = new MessageControllerApi(config);
    this._cartControllerApi = new CartControllerApi(config);
    this._subscriptionControllerApi = new SubscriptionControllerApi(config)
    this._purchaseHistoryControllerApi = new PurchaseHistoryControllerApi(config);

    this._databaseControllerApi = new DatabaseControllerApi(config);
    this._databaseTemplateControllerApi = new DatabaseTemplateControllerApi(config);
    this._databaseAdminControllerApi = new DatabaseAdminControllerApi(config);
    this._databaseSpecialOpsControllerApi = new DatabaseSpecialOpsControllerApi(config);
    this._databaseSyncControllerApi = new DatabaseSyncControllerApi(config);
  }

  get accountControllerApi(): AccountControllerApi {
    return this._accountControllerApi;
  }
  get productControllerApi(): ProductControllerApi {
    return this._productControllerApi;
  }

  get messageControllerApi(): MessageControllerApi {
    return this._messageControllerApi;
  }

  get cartControllerApi(): CartControllerApi {
    return this._cartControllerApi;
  }

  get customerControllerApi(): CustomerControllerApi {
    return this._customerControllerApi;
  }

  get smtpControllerApi(): SmtpControllerApi {
    return this._smtpControllerApi;
  }

  get orderControllerApi(): OrderControllerApi {
    return this._orderControllerApi;
  }

  get contactControllerApi(): ContactControllerApi {
    return this._contactControllerApi;
  }

  get registrationControllerApi(): RegistrationControllerApi {
    return this._registrationControllerApi;
  }

  get subscriptionControllerApi(): SubscriptionControllerApi {
    return this._subscriptionControllerApi;
  }

  get purchaseHistoryControllerApi(): PurchaseHistoryControllerApi {
    return this._purchaseHistoryControllerApi;
  }

  get voucherControllerApi(): VoucherControllerApi {
    return this._voucherControllerApi;
  }

  get organisationControllerApi(): OrganisationControllerApi {
    return this._organisationControllerApi;
  }

  get termsConditionsControllerApi(): TermsConditionsControllerApi {
    return this._termsConditionsControllerApi;
  }

  get invoiceControllerApi(): InvoicesControllerApi {
    return this._invoiceControllerApi;
  }

  get databaseControllerApi(): DatabaseControllerApi {
    return this._databaseControllerApi;
  }

  get databaseTemplateControllerApi(): DatabaseTemplateControllerApi {
    return this._databaseTemplateControllerApi;
  }

  get infoControllerApi(): InfoControllerApi {
    return this._infoControllerApi;
  }

  get databaseAdminControllerApi(): DatabaseAdminControllerApi {
    return this._databaseAdminControllerApi;
  }

  get databaseSpecialOpsControllerApi(): DatabaseSpecialOpsControllerApi {
    return this._databaseSpecialOpsControllerApi;
  }

  get databaseSyncControllerApi(): DatabaseSyncControllerApi {
    return this._databaseSyncControllerApi;
  }
}
